// caiet UC31 — the Administrator Registratorului manages the credentials of its
// own Sistem Registrator; caiet UC21-08 — the Administrator Sistemului keeps a
// register of every one of them, with the kill switch and nothing else.
// per docs/shape-uc-31-system-credentials.md §Addendum 2026-08-28 (credential lifecycle)
// per docs/shape-uc-31-credential-history.md §Approach
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslate } from '@tolgee/react';
import {
  MudButton,
  MudIcon,
  MudTextInput,
  MudModal,
  MudInfoBox,
  MudInlineMessage,
  MudSwitch,
  MudTable,
  MudTag,
} from '@egov-moldova/mud-react';
import {
  EmptyRegisterMessage,
  EmptyState,
  PageHeader,
  formatDateTime,
  onModalDismiss,
} from '@simsm/ui-shared';
import { apiErrorKey } from '../../app/api-client';
import { QueryState } from '../../components/QueryState/QueryState';
import { CredentialStatusTag } from '../../features/credentials/CredentialStatusTag';
import { SecretRevealModal } from '../../features/credentials/SecretRevealModal';
import { copyText } from '../../features/credentials/clipboard';
import {
  admittedAddresses,
  createCredential,
  listCredentials,
  rotateCredentialSecret,
  setAddressActive,
  setCredentialActive,
  setCredentialAllowlist,
} from '../../features/credentials/api';
import { inspectAddress, sameEntry } from '../../features/credentials/ip-address';
import { useSession } from '../../hooks/use-session';
import { useSessionRegistrarName } from '../../hooks/use-session-registrar-name';
import { CredentialHistoryCard } from './CredentialHistory';
import { SystemCredentialsRegisterPage } from './SystemCredentialsRegisterPage';
import './credentials.css';

const MASKED_SECRET = '•'.repeat(32);

// mud-table's own column type is not re-exported from the package root.
interface TableColumn {
  key: string;
  label: string;
  align?: 'start' | 'center' | 'end';
  width?: string;
}

export function CredentialsPage(): JSX.Element {
  const session = useSession();
  // The route is role-generic (/:role/credentials). The Administrator
  // Sistemului supervises every Sistem Registrator (UC21-08); everyone else with
  // this screen administers exactly one (UC31).
  return session.data?.activeRole === 'SYSTEM_ADMIN' ? (
    <SystemCredentialsRegisterPage />
  ) : (
    <OwnCredentialsPage />
  );
}

function OwnCredentialsPage(): JSX.Element {
  const { t } = useTranslate();
  const registrarName = useSessionRegistrarName();
  const queryClient = useQueryClient();
  const credentials = useQuery({
    queryKey: ['admin-credentials', 'own'],
    queryFn: () => listCredentials(),
  });

  const [secret, setSecret] = useState<string | null>(null);
  const [confirmRotate, setConfirmRotate] = useState(false);
  const [confirmActive, setConfirmActive] = useState(false);
  const [draftAddress, setDraftAddress] = useState('');
  const [addressTouched, setAddressTouched] = useState(false);
  // UC31-03: the outcome of the last Client ID copy; a refused clipboard is said,
  // never left as silence. docs/shape-ops-credential-copy-and-secret-date.md §2.3
  const [clientIdCopy, setClientIdCopy] = useState<'idle' | 'copied' | 'failed'>('idle');
  const [addressFailure, setAddressFailure] = useState<string | null>(null);

  const credential = credentials.data?.[0] ?? null;
  // The switch sets or lifts the Registrar's own block only (CF-48).
  const deactivating = credential !== null && !credential.blockedByRegistrar;
  const scope = credential
    ? { clientId: credential.clientId, registrarId: credential.registrarId }
    : null;

  function refresh(): void {
    void queryClient.invalidateQueries({ queryKey: ['admin-credentials'] });
  }

  const issue = useMutation({
    mutationFn: (addresses: string[]) => createCredential({ registrarId: '', addresses }),
    onSuccess: (result) => {
      setSecret(result.clientSecret);
      refresh();
    },
  });

  const rotate = useMutation({
    mutationFn: rotateCredentialSecret,
    onSuccess: (result) => {
      setConfirmRotate(false);
      setSecret(result.clientSecret);
      refresh();
    },
  });

  const toggle = useMutation({
    mutationFn: setCredentialActive,
    onSuccess: () => {
      setConfirmActive(false);
      refresh();
    },
  });

  const allowlist = useMutation({
    mutationFn: setCredentialAllowlist,
    onSuccess: refresh,
  });

  // CF-48 and the register row "activare / inactivare credențiale și IP": the
  // Registrar Administrator sets and lifts its own block on an address; a
  // block the System Administrator set is named and is not this switch's to
  // lift. docs/shape-ops-block-levels.md §6.2
  const toggleAddress = useMutation({
    mutationFn: setAddressActive,
    onSuccess: () => {
      setAddressFailure(null);
      refresh();
    },
    onError: (err: unknown) =>
      setAddressFailure(t(apiErrorKey(err, 'credentials.error.ip_toggle_failed'))),
  });

  function replaceAddresses(next: string[]): void {
    if (scope) allowlist.mutate({ ...scope, addresses: next });
  }

  const addresses = credential ? admittedAddresses(credential) : [];
  const addressColumns: TableColumn[] = [
    { key: 'address', label: t('credentials.ips.column.address') },
    { key: 'format', label: t('credentials.ips.column.format') },
    { key: 'state', label: t('credentials.ips.column.state') },
    { key: 'actions', label: t('credentials.register.actions'), align: 'end' },
  ];
  const trimmed = draftAddress.trim();
  const verdict = inspectAddress(trimmed);
  const duplicate = addresses.some((entry) => sameEntry(entry.address, trimmed));
  const addressError = !trimmed
    ? t('credentials.ips.error.empty')
    : !verdict.valid
      ? t(
          verdict.reason === 'host_bits'
            ? 'credentials.ips.error.host_bits'
            : 'credentials.ips.error.format',
        )
      : duplicate
        ? t('credentials.ips.error.duplicate')
        : '';

  function addAddress(): void {
    setAddressTouched(true);
    if (addressError || !credential) return;
    replaceAddresses([...addresses.map((entry) => entry.address), trimmed]);
    setDraftAddress('');
    setAddressTouched(false);
  }

  function removeAddress(address: string): void {
    replaceAddresses(
      addresses.filter((entry) => entry.address !== address).map((entry) => entry.address),
    );
  }

  async function copyClientId(): Promise<void> {
    if (!credential) return;
    setClientIdCopy((await copyText(credential.clientId)) ? 'copied' : 'failed');
  }

  return (
    <section className="simsm-page simsm-syscred">
      {/* Owner decision D5. AdminCredentiale.tsx:68 addresses the Registrar this screen
          administers by name; the lede is omitted rather than half-written until the session
          resolves it.

          The same wireframe line puts the state of the credential inside the H1
          — `Sistem Registrator [Activ]` — and the application had it only on the
          "Acces API" card below, which the unconfigured screen does not render at
          all (specs/039-admin-screens §A-23). Three states, and the third is the
          reason the tag has to be here: a Sistem Registrator with no credential
          reads "Neconfigurat", never "Inactiv" (which would claim a credential
          exists and is switched off) and never nothing at all (which would leave
          the reader to guess which of the two it is). The tag waits for the query
          to answer rather than announcing "Neconfigurat" while it is still
          loading. */}
      <PageHeader
        title={
          <>
            {t('credentials.own.title')}
            {credentials.isSuccess ? (
              <CredentialStatusTag
                configured={credential !== null}
                active={credential?.active ?? false}
                blockedBySystemAdmin={credential?.blockedBySystemAdmin ?? false}
                blockedByRegistrar={credential?.blockedByRegistrar ?? false}
              />
            ) : null}
          </>
        }
        lede={registrarName ? t('credentials.own.lede', { name: registrarName }) : undefined}
      />

      <QueryState
        query={credentials}
        isEmpty={credential === null}
        empty={
          /* The one screen that already had a designed empty, in a shape only it
             used. Same three parts, now the shape every other screen gives. */
          <EmptyState
            icon="shield-keyhole"
            title={t('credentials.unset.title')}
            body={t('credentials.unset.body')}
            action={
              <MudButton
                shape="circular"
                variant="primary"
                size="md"
                disabled={issue.isPending}
                onClick={() => issue.mutate([])}
              >
                <MudIcon slot="icon-start" name="shield-keyhole" size={20} />
                {t('credentials.action.set')}
              </MudButton>
            }
          />
        }
      >
        {credential ? (
          <>
            {/* CF-48, UC31-06: a block the System Administrator set is named, with
                who can lift it; the Registrar's own switch below cannot. */}
            {credential.blockedBySystemAdmin ? (
              <MudInfoBox
                emphasis="strong"
                variant="warning"
                titleText={t('credentials.own.blocked_by_system_admin.title')}
              >
                {t('credentials.own.blocked_by_system_admin.body')}
              </MudInfoBox>
            ) : !credential.active ? (
              <MudInfoBox
                emphasis="strong"
                variant="warning"
                titleText={t('credentials.own.inactive.title')}
              >
                {t('credentials.own.inactive.body')}
              </MudInfoBox>
            ) : null}

            <section className="simsm-syscred__card">
              <div className="simsm-syscred__card-head">
                <div>
                  <h2 className="simsm-syscred__card-title">
                    {t('credentials.api_access.title')}{' '}
                    <CredentialStatusTag
                      configured
                      active={credential.active}
                      blockedBySystemAdmin={credential.blockedBySystemAdmin}
                      blockedByRegistrar={credential.blockedByRegistrar}
                    />
                  </h2>
                  <p className="simsm-syscred__card-lede">{t('credentials.api_access.lede')}</p>
                </div>
                {/* The words go in the `label` slot, not the `label` prop: the
                    prop is an accessible-name fallback that renders no visible
                    text (mud-switch), and the wireframe puts "Permite
                    autentificarea API" beside the control (AdminCredentiale.tsx:74). */}
                {/* The Registrar Administrator's own block (CF-48): on while it has not
                    blocked, whatever the System Administrator did. */}
                <MudSwitch
                  checked={!credential.blockedByRegistrar}
                  disabled={toggle.isPending}
                  onMudChange={() => setConfirmActive(true)}
                >
                  <span slot="label">{t('credentials.api_access.toggle')}</span>
                </MudSwitch>
              </div>

              <div className="simsm-syscred__pair">
                <div className="simsm-syscred__field">
                  <div className="simsm-syscred__field-row">
                    <MudTextInput
                      size="md"
                      readonly
                      label={t('credentials.field.client_id')}
                      value={credential.clientId}
                    />
                    <MudButton
                      shape="circular"
                      variant="neutral"
                      size="md"
                      iconOnly
                      label={t('credentials.action.copy_client_id')}
                      onClick={() => void copyClientId()}
                    >
                      <MudIcon slot="icon" name="copy" size={20} />
                    </MudButton>
                  </div>
                  <MudInlineMessage
                    className="simsm-syscred__hint-message"
                    variant={clientIdCopy === 'failed' ? 'error' : 'info'}
                    size="small"
                    hideIcon
                    role={clientIdCopy === 'failed' ? 'alert' : undefined}
                  >
                    {clientIdCopy === 'failed'
                      ? t('credentials.field.client_id.copy_failed')
                      : clientIdCopy === 'copied'
                        ? t('credentials.field.client_id.copied')
                        : t('credentials.field.client_id.hint')}
                  </MudInlineMessage>
                </div>

                <div className="simsm-syscred__field">
                  <div className="simsm-syscred__field-row">
                    <MudTextInput
                      size="md"
                      readonly
                      label={t('credentials.field.client_secret')}
                      value={MASKED_SECRET}
                    />
                    <MudButton
                      shape="circular"
                      variant="neutral"
                      size="md"
                      disabled={rotate.isPending}
                      onClick={() => setConfirmRotate(true)}
                    >
                      {t('credentials.action.regenerate')}
                    </MudButton>
                  </div>
                  <span className="simsm-syscred__hint">
                    {/* When the current secret was set, issued or rotated; never
                        `updatedAtIso`, which every IP edit moves.
                        docs/shape-ops-credential-copy-and-secret-date.md §2.4 */}
                    {t('credentials.field.client_secret.hint', {
                      date: formatDateTime(credential.secretSetAtIso ?? credential.createdAtIso),
                    })}
                  </span>
                </div>
              </div>
            </section>

            <section className="simsm-syscred__card">
              <div className="simsm-syscred__card-head">
                <div>
                  <h2 className="simsm-syscred__card-title">{t('credentials.ips.title')}</h2>
                  <p className="simsm-syscred__card-lede">
                    {addresses.length === 1
                      ? t('credentials.ips.lede_one')
                      : t('credentials.ips.lede_other', { count: addresses.length })}
                  </p>
                </div>
                <div className="simsm-syscred__add">
                  <div className="simsm-syscred__field-row">
                    <MudTextInput
                      size="md"
                      label={t('credentials.ips.field')}
                      placeholder={t('credentials.ips.placeholder')}
                      value={draftAddress}
                      invalid={addressTouched && addressError !== ''}
                      onMudInput={(e: CustomEvent<{ value: string }>) => {
                        setDraftAddress(e.detail.value);
                        setAddressTouched(false);
                      }}
                    />
                    <MudButton
                      shape="circular"
                      variant="primary"
                      size="md"
                      disabled={allowlist.isPending}
                      onClick={addAddress}
                    >
                      {t('credentials.ips.add')}
                    </MudButton>
                  </div>
                  <MudInlineMessage
                    className="simsm-syscred__hint-message"
                    variant={addressTouched && addressError ? 'error' : 'info'}
                    size="small"
                    hideIcon
                    role={addressTouched && addressError ? 'alert' : undefined}
                  >
                    {addressTouched && addressError
                      ? addressError
                      : verdict.valid
                        ? t('credentials.ips.hint_detected', { format: verdict.format })
                        : t('credentials.ips.hint')}
                  </MudInlineMessage>
                </div>
              </div>

              {addressFailure ? (
                <MudInfoBox emphasis="strong" role="alert" variant="error">
                  {addressFailure}
                </MudInfoBox>
              ) : null}

              <div className="simsm-syscred__table">
                <MudTable
                  className="simsm-table"
                  columns={addressColumns}
                  rows={addresses.map((entry) => ({
                    id: entry.address,
                    address: entry.address,
                    format: entry.format,
                    state: entry.blockedBySystemAdmin
                      ? t('credentials.ips.state.blocked_by_system_admin')
                      : entry.blockedByRegistrar
                        ? t('credentials.ips.state.inactive')
                        : t('credentials.ips.state.active'),
                    actions: '',
                  }))}
                  rowStyle="divided"
                  disableSort={true}
                  aria-label={t('credentials.ips.table.aria_label')}
                >
                  <EmptyRegisterMessage>{t('credentials.ips.empty')}</EmptyRegisterMessage>
                  <span slot="header-cell-actions" className="visually-hidden">
                    {t('credentials.register.actions')}
                  </span>
                  {addresses.map((entry, index) => [
                    <span
                      key={`address-${entry.address}`}
                      slot={`cell-address-${index}`}
                      className="simsm-syscred__mono"
                    >
                      {entry.address}
                    </span>,
                    <span
                      key={`state-${entry.address}`}
                      slot={`cell-state-${index}`}
                      className="simsm-syscred__ip-state"
                    >
                      {/* The Registrar's own block: on while it has not blocked the
                          address, whatever the System Administrator did. The words
                          go in the `label` slot; the prop renders no visible text. */}
                      <MudSwitch
                        checked={!entry.blockedByRegistrar}
                        disabled={toggleAddress.isPending}
                        aria-label={t('credentials.register.ips.toggle', {
                          address: entry.address,
                        })}
                        onMudChange={() => {
                          if (scope) {
                            toggleAddress.mutate({
                              ...scope,
                              address: entry.address,
                              active: entry.blockedByRegistrar,
                            });
                          }
                        }}
                      >
                        <span slot="label">
                          {entry.blockedByRegistrar
                            ? t('credentials.ips.state.inactive')
                            : t('credentials.ips.state.active')}
                        </span>
                      </MudSwitch>
                      {entry.blockedBySystemAdmin ? (
                        <MudTag
                          variant="status"
                          type="subtle"
                          size="sm"
                          semantic="accent"
                          label={t('credentials.ips.state.blocked_by_system_admin')}
                        />
                      ) : null}
                    </span>,
                    <span key={`actions-${entry.address}`} slot={`cell-actions-${index}`}>
                      <MudButton
                        shape="circular"
                        variant="destructive"
                        appearance="text"
                        size="sm"
                        disabled={allowlist.isPending}
                        onClick={() => removeAddress(entry.address)}
                      >
                        {t('credentials.ips.action.remove')}
                      </MudButton>
                    </span>,
                  ])}
                </MudTable>
              </div>
            </section>

            <CredentialHistoryCard
              clientId={credential.clientId}
              registrarId={credential.registrarId}
            />
          </>
        ) : null}
      </QueryState>

      <MudModal
        closeLabel={t('common.close')}
        open={confirmRotate}
        size="md"
        titleText={t('credentials.regenerate.title')}
        // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — seven AGE
        // components emit `mudClose` bubbling+composed, so a descendant closing inside this
        // modal reaches this handler too; the guard answers only a real dismissal.
        onMudClose={onModalDismiss(() => setConfirmRotate(false))}
      >
        <MudInfoBox emphasis="strong" variant="warning">
          {t('credentials.regenerate.body')}
        </MudInfoBox>
        <div slot="actions" className="simsm-syscred__modal-actions">
          <MudButton
            shape="circular"
            variant="neutral"
            size="md"
            onClick={() => setConfirmRotate(false)}
          >
            {t('credentials.common.cancel')}
          </MudButton>
          <MudButton
            shape="circular"
            variant="primary"
            size="md"
            disabled={rotate.isPending}
            onClick={() => {
              if (scope) rotate.mutate(scope);
            }}
          >
            {t('credentials.regenerate.confirm')}
          </MudButton>
        </div>
      </MudModal>

      <MudModal
        closeLabel={t('common.close')}
        open={confirmActive}
        size="md"
        titleText={
          deactivating
            ? t('credentials.toggle.deactivate.title')
            : t('credentials.toggle.activate.title')
        }
        // AGE-BUG(mudClose-bubbles) · vendor/age-design/SOURCE_COMMIT 7322a157 — seven AGE
        // components emit `mudClose` bubbling+composed, so a descendant closing inside this
        // modal reaches this handler too; the guard answers only a real dismissal.
        onMudClose={onModalDismiss(() => setConfirmActive(false))}
      >
        <MudInfoBox emphasis="strong" variant={deactivating ? 'warning' : 'info'}>
          {deactivating
            ? t('credentials.toggle.deactivate.body')
            : credential?.blockedBySystemAdmin
              ? t('credentials.toggle.activate.body_blocked_by_system_admin')
              : t('credentials.toggle.activate.body')}
        </MudInfoBox>
        <div slot="actions" className="simsm-syscred__modal-actions">
          <MudButton
            shape="circular"
            variant="neutral"
            size="md"
            onClick={() => setConfirmActive(false)}
          >
            {t('credentials.common.cancel')}
          </MudButton>
          <MudButton
            shape="circular"
            variant={deactivating ? 'destructive' : 'primary'}
            size="md"
            disabled={toggle.isPending}
            onClick={() => {
              if (scope) toggle.mutate({ ...scope, active: !deactivating });
            }}
          >
            {deactivating ? t('credentials.action.deactivate') : t('credentials.action.activate')}
          </MudButton>
        </div>
      </MudModal>

      <SecretRevealModal secret={secret} onClose={() => setSecret(null)} />
    </section>
  );
}
